/**
 * Format a number as currency (USD by default).
 * @param amount - The numeric amount to format
 * @param compact - If true, use compact notation (e.g., $148.5K)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number | null | undefined, compact?: boolean): string {
    if (amount === null || amount === undefined) {
        return "N/A";
    }

    if (compact) {
        const absAmount = Math.abs(amount);
        if (absAmount >= 1_000_000) {
            return `$${(amount / 1_000_000).toFixed(1)}M`;
        }
        if (absAmount >= 1_000) {
            return `$${(amount / 1_000).toFixed(1)}K`;
        }
        return `$${amount.toFixed(0)}`;
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

/**
 * Format a number as a percentage.
 * @param value - The numeric value to format (e.g., 56.2 for 56.2%)
 * @returns Formatted percentage string
 */
export function formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return "N/A";
    }
    return `${value.toFixed(1)}%`;
}

/**
 * Format a number as months (for runway).
 * @param value - The number of months
 * @returns Formatted months string
 */
export function formatMonths(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return "N/A";
    }
    return `${value.toFixed(1)}mo`;
}

/**
 * Format a multiplier value (e.g., LTV/CAC ratio).
 * @param value - The multiplier value
 * @returns Formatted multiplier string
 */
export function formatMultiplier(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return "N/A";
    }
    return `${value.toFixed(1)}x`;
}

/**
 * Format a number as weeks (for runway).
 * @param value - The number of weeks
 * @returns Formatted weeks string
 */
export function formatWeeks(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return "N/A";
    }
    return `${value.toFixed(1)}w`;
}

/**
 * Format a relative time string (e.g., "2 minutes ago").
 * @param dateString - ISO date string
 * @returns Human-readable relative time
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
    if (!dateString) {
        return "N/A";
    }

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
        return "just now";
    }
    if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    }
    if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    }
    return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
}
